import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialComment, SocialPost } from '../../models/social-feed.model';
import { CurrentUserService } from 'src/app/services/current-user.service';

interface Thread {
  comment: SocialComment;
  replies: SocialComment[];
}

/**
 * The comment thread under a post: comments with one level of replies,
 * likes, editing (your own, for 24 hours) and delete. Used by the feed and by
 * the single-post page.
 *
 * New comments show up immediately (faded) while the request is on its way,
 * and a second click/Enter while one is sending does nothing — so a slow
 * connection can't produce a duplicate. It keeps `post.comments_count` in
 * step with the thread.
 */
@Component({
  selector: 'app-social-comments',
  templateUrl: './social-comments.component.html',
  styleUrls: ['./social-comments.component.scss'],
  standalone: false,
})
export class SocialCommentsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) post!: SocialPost;
  /** The post's comment count changed (a comment or reply was added, failed, or deleted) —
   * the page that shows the count needs to redraw. Carries the new count. */
  @Output() countChange = new EventEmitter<number>();

  @ViewChild('replyBox') replyBox?: ElementRef<HTMLInputElement>;
  @ViewChild('editBox') editBox?: ElementRef<HTMLInputElement>;

  comments: SocialComment[] = [];
  threads: Thread[] = [];
  loading = true;

  draft = '';
  /** Root comment the inline reply box is open under, if any. */
  replyingTo: number | null = null;
  replyDraft = '';
  /** Top-level comments whose replies are showing. */
  expanded = new Set<number>();

  /** A comment is on its way to the server; further sends are ignored meanwhile. */
  sending = false;
  error = '';

  editingId: number | null = null;
  editDraft = '';
  savingEdit = false;
  editError = '';

  pendingDelete: SocialComment | null = null;
  deleting = false;

  private destroyed = false;
  private likePending = new Set<number>();
  private nextTempId = 0;
  private myName = 'You';
  private myId: number | null = null;

  constructor(
    private socialFeedService: SocialFeedService,
    private currentUserService: CurrentUserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Enough to draw my own comment before the server has answered.
    this.currentUserService.currentUser.subscribe((user) => {
      this.myId = user?.id ?? null;
      this.myName = user?.display_name || user?.name || user?.username || 'You';
    });
    this.socialFeedService.getComments(this.post.id).subscribe({
      next: (comments) => {
        // Keep anything added locally while this request was in flight
        // (a fast comment on a slow connection) instead of overwriting it.
        const fetched = new Set(comments.map((c) => c.id));
        const addedMeanwhile = this.comments.filter((c) => !fetched.has(c.id));
        this.comments = [...comments, ...addedMeanwhile];
        this.loading = false;
        this.rebuild();
      },
      error: () => {
        this.loading = false;
        this.rebuild();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  /** Redraw — unless the thread was closed while a request was still in flight. */
  private refresh(): void {
    if (!this.destroyed) {
      this.cdr.detectChanges();
    }
  }

  private rebuild(): void {
    const replies = new Map<number, SocialComment[]>();
    for (const comment of this.comments) {
      if (comment.parent) {
        replies.set(comment.parent, [...(replies.get(comment.parent) || []), comment]);
      }
    }
    this.threads = this.comments
      .filter((c) => !c.parent)
      .map((comment) => ({ comment, replies: replies.get(comment.id) || [] }));
    this.refresh();
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  // -----------------------------------------------------------------------
  // Sending (new comment or reply)
  // -----------------------------------------------------------------------

  submitComment(): void {
    const text = this.draft.trim();
    if (!text || this.sending) {
      return;
    }
    this.draft = '';
    this.dispatch(text, null, (t) => {
      if (!this.draft) {
        this.draft = t;
      }
    });
  }

  submitReply(): void {
    const text = this.replyDraft.trim();
    const parentId = this.replyingTo;
    if (!text || parentId === null || this.sending) {
      return;
    }
    this.replyDraft = '';
    this.replyingTo = null;
    this.dispatch(text, parentId, (t) => {
      // Give the text (and the reply box) back so nothing is lost.
      this.replyingTo = parentId;
      if (!this.replyDraft) {
        this.replyDraft = t;
      }
    });
  }

  private dispatch(text: string, parentId: number | null, restore: (text: string) => void): void {
    this.sending = true;
    this.error = '';

    // Show it straight away (faded) so a slow request doesn't look like a dead click.
    const now = new Date().toISOString();
    const placeholder: SocialComment = {
      id: --this.nextTempId,
      post: this.post.id,
      parent: parentId,
      author: { id: this.myId ?? 0, name: this.myName, avatar: null },
      content: text,
      can_delete: false,
      can_edit: false,
      is_edited: false,
      likes_count: 0,
      liked_by_me: false,
      status: 'active',
      created_at: now,
      updated_at: now,
      pending: true,
    };
    this.comments = [...this.comments, placeholder];
    this.post.comments_count += 1;
    this.countChange.emit(this.post.comments_count);
    if (parentId) {
      this.expanded.add(parentId);
    }
    this.rebuild();

    this.socialFeedService.addComment(this.post.id, text, parentId).subscribe({
      next: (saved) => {
        // If the thread was fetched meanwhile it may already hold the saved
        // comment; don't show it twice.
        this.comments = this.comments.some((c) => c.id === saved.id)
          ? this.comments.filter((c) => c.id !== placeholder.id)
          : this.comments.map((c) => (c.id === placeholder.id ? saved : c));
        this.sending = false;
        this.rebuild();
      },
      error: () => {
        this.comments = this.comments.filter((c) => c.id !== placeholder.id);
        this.post.comments_count = Math.max(0, this.post.comments_count - 1);
        this.countChange.emit(this.post.comments_count);
        restore(text);
        this.error = "Couldn't send your comment. Please try again.";
        this.sending = false;
        this.rebuild();
      },
    });
  }

  // -----------------------------------------------------------------------
  // Replies
  // -----------------------------------------------------------------------

  /** Open the reply box under a thread. Replying to a reply files it under the
   * same top-level comment, so it starts with the person's @name. */
  startReply(comment: SocialComment): void {
    const rootId = comment.parent ?? comment.id;
    this.replyingTo = rootId;
    this.replyDraft = comment.parent ? `@${comment.author.name} ` : '';
    this.expanded.add(rootId);
    this.refresh();
    this.replyBox?.nativeElement.focus();
  }

  cancelReply(): void {
    this.replyingTo = null;
    this.replyDraft = '';
  }

  toggleReplies(rootId: number): void {
    if (this.expanded.has(rootId)) {
      this.expanded.delete(rootId);
    } else {
      this.expanded.add(rootId);
    }
  }

  // -----------------------------------------------------------------------
  // Likes
  // -----------------------------------------------------------------------

  toggleLike(comment: SocialComment): void {
    if (comment.pending || this.likePending.has(comment.id)) {
      return;
    }
    this.likePending.add(comment.id);
    const wasLiked = comment.liked_by_me;
    comment.liked_by_me = !wasLiked;
    comment.likes_count += wasLiked ? -1 : 1;

    const call = wasLiked
      ? this.socialFeedService.unlikeComment(comment.id)
      : this.socialFeedService.likeComment(comment.id);
    call.subscribe({
      next: (updated) => {
        comment.liked_by_me = updated.liked_by_me;
        comment.likes_count = updated.likes_count;
        this.likePending.delete(comment.id);
        this.refresh();
      },
      error: () => {
        comment.liked_by_me = wasLiked;
        comment.likes_count += wasLiked ? 1 : -1;
        this.likePending.delete(comment.id);
        this.refresh();
      },
    });
  }

  // -----------------------------------------------------------------------
  // Edit
  // -----------------------------------------------------------------------

  startEdit(comment: SocialComment): void {
    this.editingId = comment.id;
    this.editDraft = comment.content;
    this.editError = '';
    this.refresh();
    this.editBox?.nativeElement.focus();
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editError = '';
  }

  saveEdit(comment: SocialComment): void {
    const text = this.editDraft.trim();
    if (!text || this.savingEdit) {
      return;
    }
    if (text === comment.content) {
      this.cancelEdit();
      return;
    }
    this.savingEdit = true;
    this.editError = '';
    this.socialFeedService.editComment(comment.id, text).subscribe({
      next: (saved) => {
        comment.content = saved.content;
        comment.is_edited = saved.is_edited;
        comment.updated_at = saved.updated_at;
        this.editingId = null;
        this.savingEdit = false;
        this.refresh();
      },
      error: () => {
        this.editError = "Couldn't save your edit. Comments can only be edited for 24 hours.";
        this.savingEdit = false;
        this.refresh();
      },
    });
  }

  // -----------------------------------------------------------------------
  // Report
  // -----------------------------------------------------------------------

  /** Never your own comment; a still-sending one has no id to report yet. */
  canReport(comment: SocialComment): boolean {
    return !comment.pending && comment.author.id !== this.myId;
  }

  pendingReport: { commentId: number } | null = null;

  askReport(comment: SocialComment): void {
    this.pendingReport = { commentId: comment.id };
  }

  closeReport(): void {
    this.pendingReport = null;
  }

  // -----------------------------------------------------------------------
  // Delete
  // -----------------------------------------------------------------------

  askDelete(comment: SocialComment): void {
    this.pendingDelete = comment;
  }

  cancelDelete(): void {
    if (!this.deleting) {
      this.pendingDelete = null;
    }
  }

  get deleteMessage(): string {
    const replies = this.pendingDelete ? this.comments.filter((c) => c.parent === this.pendingDelete!.id).length : 0;
    return replies > 0
      ? `This comment and its ${replies === 1 ? 'reply' : `${replies} replies`} will be permanently removed. This can't be undone.`
      : "This comment will be permanently removed. This can't be undone.";
  }

  confirmDelete(): void {
    const target = this.pendingDelete;
    if (!target || this.deleting) {
      return;
    }
    this.deleting = true;
    this.socialFeedService.deleteComment(target.id).subscribe({
      next: () => {
        // Deleting a comment takes its replies with it.
        const gone = this.comments.filter((c) => c.id === target.id || c.parent === target.id).length;
        this.comments = this.comments.filter((c) => c.id !== target.id && c.parent !== target.id);
        this.post.comments_count = Math.max(0, this.post.comments_count - gone);
        this.countChange.emit(this.post.comments_count);
        this.pendingDelete = null;
        this.deleting = false;
        this.rebuild();
      },
      error: () => {
        this.deleting = false;
        this.refresh();
      },
    });
  }
}
